import styled, { css } from 'styled-components';

import { PerfectScrollbar } from '@components';
import { TransientProps } from '@common/types';

type ContainerProps = TransientProps<{ disabled: boolean; hide: boolean }>;

export const Container = styled.div<ContainerProps>`
  ${({ $disabled: isDisabled, $hide: shouldHide }) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
    transition: 180ms ease-in-out;

    ${isDisabled &&
    css`
      opacity: 0;
      pointer-events: none;
      user-select: none;
    `}

    ${shouldHide &&
    css`
      display: none;
    `}
  `}
`;

export const ResultsContainer = styled.div<ContainerProps>`
  ${({ $disabled: isDisabled, $hide: shouldHide }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: 180ms ease-in-out;
    overflow-y: auto;

    ${isDisabled &&
    css`
      opacity: 0;
      pointer-events: none;
      user-select: none;
    `}

    ${shouldHide &&
    css`
      display: none;
    `}
  `}
`;

type MessageContainerProps = Partial<TransientProps<{ success: boolean }>>;

export const MessageContainer = styled.div<MessageContainerProps>`
  ${({ theme, $success: isSuccess }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 1rem;
    height: calc(100% - 5.875rem);

    svg {
      color: ${isSuccess
        ? theme.colors.success[100]
        : theme.colors.brand.primary.default};
    }
  `}
`;

export const PollWrapper = styled(PerfectScrollbar)`
  ${({ theme }) => css`
    width: 100%;

    h3 {
      display: flex;
      gap: 0.5rem;
      padding: 1rem;
      font-size: 1rem;
      font-weight: 500;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid ${theme.colors.background[80]};

      > div {
        overflow: hidden;
      }
    }
  `}
`;

export const PollOptionsContainer = styled.ul`
  ${() => css`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.5rem;
    padding: 0 1rem;
  `}
`;

type PollOptionProps = Partial<TransientProps<{ locked: boolean }>>;

export const PollOption = styled.li<PollOptionProps>`
  ${({ theme, $locked: isLocked }) => css`
    flex: 1;

    > button {
      width: 100%;
      border-radius: 0.5rem;
      justify-content: flex-start;
      gap: 1rem;
      background: ${theme.colors.background[60]};
      border: 1px solid ${theme.colors.background[40]};
      transition: 180ms 80ms ease-in-out;
      cursor: ${isLocked ? 'not-allowed' : 'pointer'};
      opacity: ${isLocked ? 0.6 : 1};

      > img {
        width: 3.5rem;
        height: 3.5rem;
        border-radius: 1rem;
        object-fit: cover;
      }

      :hover {
        border-color: ${isLocked
          ? theme.colors.background[40]
          : theme.colors.brand.primary.default};
      }
    }
  `}
`;

export const ResultsNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.5rem;
`;

export const ResultsNavButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.75rem;
    background: ${theme.colors.brand.primary.default};
    border: none;
    border-radius: ${theme.radii.default};
    font-size: 0.75rem;
    font-weight: 500;
    color: ${theme.colors.brand.primary.text};
    cursor: pointer;
    transition: all 150ms ease;

    &:hover:not(:disabled) {
      background: ${theme.colors.brand.primary.hover};
    }

    &:disabled {
      background: ${theme.colors.brand.primary.disabled};
      opacity: 0.4;
      cursor: not-allowed;
    }
  `}
`;

export const ResultsCounter = styled.span`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const PollResult = styled.li`
  ${({ theme }) => css`
    flex: 1;

    > div {
      width: 100%;
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.5rem;
      position: relative;
      background: ${theme.colors.background[60]};
      border: 1px solid ${theme.colors.brand.primary.default};
      color: ${theme.colors.brand.primary.default};
      transition: 180ms 80ms ease-in-out;

      > div {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: ${theme.colors.brand.primary.opaque};
      }
    }
  `}
`;
